import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { assignMetadata } from '../../base/metadata';
import {
  connectIntegration,
  disableIntegration,
  disconnectIntegration,
  enableIntegration,
  updateIntegrationFunctions,
} from './integration.actions';
import type { ClientConnection, IntegrationConfig } from './integration.schema';

// Create entity adapter for normalized state management
// Now using provider as the ID since we have one config per provider
export const integrationsAdapter = createEntityAdapter<
  IntegrationConfig,
  string
>({
  selectId: (integration: IntegrationConfig) => integration.provider,
  sortComparer: (a: IntegrationConfig, b: IntegrationConfig) =>
    a.provider.localeCompare(b.provider),
});

const initialState = integrationsAdapter.getInitialState();

const slice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(connectIntegration, (state, action) => {
      const {
        provider,
        paragonConnectionId,
        paragonUserId,
        clientEnv,
        metadata,
      } = action.payload;

      // Create the connection with metadata
      const newConnection: ClientConnection = {
        id: paragonConnectionId,
        paragonConnectionId,
        paragonUserId,
        provider,
        metadata,
        createdAt: '',
        createdBy: '',
        updatedAt: undefined,
        updatedBy: undefined,
      };

      const connectionWithMetadata = assignMetadata(action, newConnection);

      // Check if we already have a config for this provider
      const existingConfig = state.entities[provider];

      if (existingConfig) {
        // Update existing config: add connection to pool and reference in environment
        integrationsAdapter.updateOne(state, {
          id: provider,
          changes: {
            connections: {
              ...existingConfig.connections,
              [paragonConnectionId]: connectionWithMetadata,
            },
            clientEnvs: {
              ...existingConfig.clientEnvs,
              [clientEnv]: paragonConnectionId,
            },
          },
        });
      } else {
        // Create new provider config with the connection in pool
        const newConfig: IntegrationConfig = {
          provider: provider as any, // Type assertion needed for discriminated union
          availableFunctions: [], // Empty list - functions will be added as needed
          timeout: 30000,
          connections: {
            [paragonConnectionId]: connectionWithMetadata,
          },
          clientEnvs: {
            sandbox: clientEnv === 'sandbox' ? paragonConnectionId : undefined,
            preRelease:
              clientEnv === 'preRelease' ? paragonConnectionId : undefined,
            live: clientEnv === 'live' ? paragonConnectionId : undefined,
          },
        };

        integrationsAdapter.addOne(state, newConfig);
      }
    });

    builder.addCase(disconnectIntegration, (state, action) => {
      const { provider, paragonConnectionId } = action.payload;

      // Get the specific provider config
      const config = state.entities[provider];
      if (!config) {
        return;
      }

      // Check if this connection exists in this provider's pool
      if (!config.connections[paragonConnectionId]) {
        return;
      }

      // Remove connection from pool
      const updatedConnections = { ...config.connections };
      delete updatedConnections[paragonConnectionId];

      // Clear all environment references to this connection
      const updatedEnvs = { ...config.clientEnvs };
      Object.entries(config.clientEnvs).forEach(([env, connId]) => {
        if (connId === paragonConnectionId) {
          updatedEnvs[env as keyof typeof updatedEnvs] = undefined;
        }
      });

      // Check if this was the last connection for this provider
      const hasAnyConnection = Object.keys(updatedConnections).length > 0;

      if (!hasAnyConnection) {
        // Remove the entire provider config if no connections remain
        integrationsAdapter.removeOne(state, config.provider);
      } else {
        // Update with removed connection and cleared references
        integrationsAdapter.updateOne(state, {
          id: config.provider,
          changes: {
            connections: updatedConnections,
            clientEnvs: updatedEnvs,
          },
        });
      }
    });

    builder.addCase(enableIntegration, (state, action) => {
      const { provider, paragonConnectionId, clientEnv } = action.payload;
      const existingConfig = state.entities[provider];

      if (existingConfig) {
        // Check if the connection exists in the pool
        const connection = existingConfig.connections[paragonConnectionId];

        if (connection) {
          // Only update the target environment reference
          integrationsAdapter.updateOne(state, {
            id: provider,
            changes: {
              clientEnvs: {
                ...existingConfig.clientEnvs,
                [clientEnv]: paragonConnectionId,
              },
            },
          });
        }
      }
    });

    builder.addCase(disableIntegration, (state, action) => {
      const { provider, paragonConnectionId, clientEnv } = action.payload;
      const existingConfig = state.entities[provider];

      if (existingConfig) {
        const connectionId = existingConfig.clientEnvs[clientEnv];

        // Only clear the environment reference if it matches the specified connection
        if (connectionId === paragonConnectionId) {
          integrationsAdapter.updateOne(state, {
            id: provider,
            changes: {
              clientEnvs: {
                ...existingConfig.clientEnvs,
                [clientEnv]: undefined,
              },
            },
          });
        }
      }
    });

    builder.addCase(updateIntegrationFunctions, (state, action) => {
      const { provider, availableFunctions } = action.payload;
      const existingConfig = state.entities[provider];

      if (existingConfig) {
        integrationsAdapter.updateOne(state, {
          id: provider,
          changes: {
            availableFunctions,
          },
        });
      }
    });
  },
});

export default slice.reducer;
export const integrationsReducer = slice.reducer;
